import { HealthNavigatorAuthorMessage, PostMessageType, RATING_TYPE } from '@abiglobalhealth/go-types'
import { FormEvent, PropsWithChildren, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { deleteHealthNavigatorMessageFeedback, storeHealthNavigatorMessageFeedback } from '@/api/navigator'
import { Button } from '@/components/Button'
import { ThumbDownIcon } from '@/components/icons/ThumbDownIcon'
import { ThumbUpIcon } from '@/components/icons/ThumbUpIcon'
import { Modal } from '@/components/Modal'
import useNavigator from '@/hooks/useNavigator'
import { usePostMessage } from '@/hooks/usePostMessage'
import { useToast } from '@/hooks/useToast'
import { useUser } from '@/hooks/useUser'
import { isAbortError } from '@/utils/errors'

export interface MessageFeedbackProps {
  message: HealthNavigatorAuthorMessage
}

interface FeedbackCommentModalProps {
  onClose: () => void
  onSubmit: (comment: string) => Promise<void>
  isSubmitting: boolean
}

type FeedbackButtonProps = PropsWithChildren<{
  disabled: boolean
  onClick: () => void
}>

const FeedbackButton = ({ children, disabled, onClick }: FeedbackButtonProps) => (
  <button
    type="button"
    data-testid="navigator-feedback-button"
    className="text-gray-text flex size-[30px] items-center justify-center p-[7px]"
    disabled={disabled}
    onClick={onClick}
  >
    {children}
  </button>
)

const FeedbackCommentModal = ({ onClose, onSubmit, isSubmitting }: FeedbackCommentModalProps) => {
  const { t } = useTranslation()
  const [comment, setComment] = useState<string>('')

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    await onSubmit(comment)
  }

  return (
    <Modal isOpen title={t('navigator.reply_feedback.modal.title')!} onClose={onClose} dataTestId="navigator-feedback-comment-modal">
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        <textarea
          className="border-gray focus-visible:border-primary w-full resize-none rounded-lg border p-4 text-base transition-[border-color] focus-visible:outline-hidden"
          data-testid="navigator-feedback-comment-textarea"
          rows={3}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t('navigator.reply_feedback.modal.placeholder')!}
          autoFocus
        />
        <Button disabled={isSubmitting} loading={isSubmitting} hasShadow={false} data-testid="navigator-feedback-comment-submit-button">
          {t('navigator.reply_feedback.modal.button_name')}
        </Button>
      </form>
    </Modal>
  )
}

export const MessageFeedback = ({ message }: MessageFeedbackProps) => {
  const { t } = useTranslation()
  const { user } = useUser()
  const { navigator, setNavigator } = useNavigator()
  const { sendPostMessage } = usePostMessage()
  const { show: showToast } = useToast()
  const [showModal, setShowModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)

  const currentRating = message.userFeedback?.rating ?? null
  const feedbackId = message.userFeedback?.feedbackId

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort()
    }
  }, [])

  const submitFeedback = async (rating: number, comment?: string) => {
    const messageId = message.messageId!
    const uniqueId = user?.data.uniqueId!

    try {
      setIsSubmitting(true)
      abortControllerRef.current = new AbortController()

      if (currentRating === rating && feedbackId && !comment) {
        const response = await deleteHealthNavigatorMessageFeedback({ uniqueId, feedbackId, messageId }, abortControllerRef.current.signal)
        setNavigator(response)
        await sendPostMessage(PostMessageType.NAVIGATOR_MESSAGE_FEEDBACK_DELETED, {
          messageId,
          conversationId: navigator.healthNavigator.conversationId!
        })
      } else {
        const ratingObject = { messageId, ratingType: RATING_TYPE.THUMBS, rating, comment }
        const response = await storeHealthNavigatorMessageFeedback(
          { uniqueId, feedbackId: feedbackId ?? null, ...ratingObject },
          abortControllerRef.current.signal
        )
        setNavigator(response)
        await sendPostMessage(PostMessageType.NAVIGATOR_MESSAGE_FEEDBACK_SUBMITTED, {
          ...ratingObject,
          conversationId: navigator.healthNavigator.conversationId!
        })
        showToast(t('navigator.reply_feedback.modal.toast_message'))
      }
    } catch (error: unknown) {
      if (!isAbortError(error)) throw error
    } finally {
      setShowModal(false)
      setIsSubmitting(false)
      abortControllerRef.current = null
    }
  }

  const isThumbUp = currentRating === 1
  const isThumbDown = currentRating === -1

  const handleThumbDown = () => {
    if (isThumbDown && feedbackId) return submitFeedback(-1)
    setShowModal(true)
  }

  if (!message.messageId) return null

  return (
    <>
      <div className="flex items-center gap-3 pl-10">
        {(!currentRating || isThumbUp) && (
          <FeedbackButton disabled={isSubmitting} onClick={() => submitFeedback(1)}>
            <ThumbUpIcon filled={isThumbUp} />
          </FeedbackButton>
        )}
        {(!currentRating || isThumbDown) && (
          <FeedbackButton disabled={isSubmitting} onClick={handleThumbDown}>
            <ThumbDownIcon filled={isThumbDown} />
          </FeedbackButton>
        )}
      </div>

      {showModal && (
        <FeedbackCommentModal onClose={() => submitFeedback(-1)} onSubmit={(comment) => submitFeedback(-1, comment)} isSubmitting={isSubmitting} />
      )}
    </>
  )
}
